/* eslint-disable no-param-reassign */
import i18next from 'i18next';
import { DateTime } from 'luxon';
import { YearAndMonth, AmountValue } from '../common/interfaces';
import { AccountStatus, Currency } from '../enums';
import { AccountDate } from 'stores/accountsStore';
import { PortfolioType, PortfolioCode } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { AssetCategory } from 'common/interfaces';
import { GLOBAL_ERR_PREFIX } from 'stores/apiStore';

// converts share number to readable percent value
export function fractionToPercent(fraction: number, decimals?: number, numberOnly?: boolean): string {
  return `${(fraction * 100).toFixed(decimals !== undefined ? decimals : 1).replace('.', ',')}${
    numberOnly ? '' : ' %'
  }`;
}

export const f2p = (n: number): number => Math.round(n*10000)/100;
//export const p2f = (n: number): number => Math.round(n*10000)/100;


// Every printing of money with currency should go thru this.
export const formatAmountValue = (
  value: number,
  currency?: Currency | null,
  balance?: boolean,
  shorten?: boolean,
): string => {
  let short = null;
  if (shorten) {
    if (Math.abs(value) >= 1000000000) {
      value = value / 1000000000;
      short = 'billion';
    } else if (Math.abs(value) >= 1000000) {
      value = value / 1000000;
      short = 'million';
    } else if (Math.abs(value) >= 100000) {
      value = value / 1000;
      short = 'thousand';
    } else if (Math.abs(value) >= 10000 && currency === null) {
      value = value / 1000;
      short = 'thousand';
    }
  }
  if (currency === undefined) currency = Currency.CZK;
  switch (currency) {
    case Currency.CZK:
    default:
      return `${balance && value > 0 ? '+ ' : ''}${value.toLocaleString(i18next.language, {
        minimumFractionDigits: shorten ? undefined : value % 1 !== 0 ? 2 : undefined,
        maximumFractionDigits: shorten ? 1 : 2,
      })} ${short !== null ? i18next.t(`number_${short}`) + ' ' : ''}${
        currency ? i18next.t(`currency_${currency}`, currency) : ''
      }`;
  }
};

export const formatAmount = (value: { value: number }, currency: Currency, balance?: boolean): string => {
  return formatAmountValue(value.value, currency, balance);
};

export const formatNumber = (value: number, decimals?: number): string => {
  return `${value.toLocaleString(i18next.language, {
    minimumFractionDigits: decimals || undefined,
    maximumFractionDigits: decimals || undefined,
  })}`;
};

export const jsDateToLocaleString = (jsDate: Date | number): string =>
  DateTime.fromJSDate(typeof jsDate === 'number' ? new Date(jsDate) : jsDate)
    .setLocale('cs-CZ')
    .toLocaleString({
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });

export const pluralize = (count: number | string, str1: string, str2: string, str0?: string): string => {
  // Fix formik number field conversion to string
  if (typeof count === 'string') {
    count = parseInt(count);
  }
  switch (count) {
    case 1:
      return str1;
    case 2:
    case 3:
    case 4:
      return str2;
    default:
      return str0 ? str0 : str2;
  }
};

// convert object of validation errors to FormikError format
// validationPrefixDepth is depth of error key that should be scraped for propper mapping
// to string used as a name of fields
export const transformToFormErrors = (errors: any, validationPrefixDepth: number) => {
  // make enries from errors, so it can be created object matching Formik's error format
  const entries = errors.map((error: { key: string }) => {
    const pathArray = error.key.split('.').filter((n) => n !== ''); // ! odstranit filtr, až přestanou chodit věci typu account.basic..title se dvěmi tečkami

    // make key from by removint prefixes and error type from the key in the response error
    const key = pathArray
      .filter((pathFragment) => !!pathFragment === true)
      .slice(validationPrefixDepth, pathArray.length - 1)
      .join('.');
    return [key, error.key] as [string, string];
  });

  // create FormikErrors object that consists of name of the value and full error key from backend
  // the component itself then decide if it should use the whole key for specific error or just a type
  return Object.fromEntries(entries);
};

export function getErrorMessageKey(errorKey: string, specificError: boolean = false) {
  if (errorKey) {
    if (typeof errorKey !== 'string') {
      return 'formfieldError_invalid'; // ošetření pro případ špatného validašního klíče (viz RA-915)
    }
    const path: string[] = errorKey.split('.');
    const errorType: string = path.slice(-1)[0];
    return specificError ? [`formfieldError_${errorKey}`,`formfieldError_${errorType}`] : `formfieldError_${errorType}`;
  }
  return '';
}

function flattenErrObj(obj: { [key: string]: any }, parent?: string, res: string[] | undefined = []) { //prefixDepth?: number,
  for (let key in obj) {
    let propName = parent ? parent + '.' + key : key;
    if (typeof obj[key] == 'object') {
      flattenErrObj(obj[key], propName, res); //prefixDepth,
    } else {
      //res.push(`${prefixDepth ? new Array(prefixDepth).fill(0).reduce((p, i) => p + 'prefix.', '') : ''}${propName}`);
      res.push(obj[propName])
    }
  }
  return res;
}

export function validationErrToForm(formPath?: Object) {
  if (!formPath) return undefined;
  if (typeof formPath === 'string') {
    return ['validationError_' + formPath];
  }
  return flattenErrObj(formPath).map(err => 'validationError_' + err);
}

export function globalErrToForm(formPath?: Object) {
  if (!formPath) return undefined;
  if (typeof formPath === 'string') {
    return ['globalErrors_' + formPath.substr(GLOBAL_ERR_PREFIX.length)];
  }
  return flattenErrObj(formPath).map(err => 'globalErrors_' + err.substr(GLOBAL_ERR_PREFIX.length));
}



export function sqlDateToLabel(date: string, format?: string): string {
  return DateTime.fromSQL(date)
    .setLocale(i18next.language)
    .toFormat(format || 'LLLL yyyy');
}

export function isoDateTimeToLabel(datetime: string, format?: string): string {
  return DateTime.fromISO(datetime)
    .setLocale(i18next.language)
    .toFormat(format || 'dd. MM. yyyy HH:mm');
}

export function isoDateTimeToLabelZoned(datetime: string, format?: string): string {
  return DateTime.fromISO(datetime)
    .setZone('Europe/Prague')
    .setLocale(i18next.language)
    .toFormat(format || 'dd. MM. yyyy HH:mm');
}

export function getYearsAndMonthsFromLuxon(date: DateTime): YearAndMonth {
  return {
    years: date.year,
    months: date.month,
  };
}

export function sqlDateToYearAndMonth(date: string): YearAndMonth {
  const DateTimeObject = DateTime.fromSQL(date);
  return {
    years: DateTimeObject.year,
    months: DateTimeObject.month,
  };
}

export function snakeCaseToUpperCase(s: string): string {
  return s
    .split(/(?=[A-Z])/)
    .join('_')
    .toUpperCase();
}

export function scrollToTop(selector?: string) {
  selector ? document.querySelector(selector)?.scrollTo(0, 0) : window.scrollTo(0, 0);
}

export function replaceAt(string: string, index: number, replacement: string) {
  return string.substr(0, index) + replacement + string.substr(index + replacement.length);
}

export function splice(string: string, start: number, delCount: number, newSubStr: string) {
  return string.slice(0, start) + newSubStr + string.slice(start + Math.abs(delCount));
}

// comparing SQL dates (strings) for sorting
export const compareSqlDates = (firstDate: string, secondDate: string) => {
  if (firstDate === secondDate) return 0;
  return firstDate < secondDate ? -1 : 1;
};

export const yearAndMonthToSql = (year: string | number, month: string | number): string =>
  `${year}-${`${month}`.padStart(2, '0')}-01`;

export const isDraftOrMock = (status: AccountStatus) => [AccountStatus.DRAFT, AccountStatus.MOCK].includes(status);

export const compareDatesFormat = (
  firstDate: string,
  secondDate: string,
  format: string,
  emptyLast?: boolean,
): -1 | 1 | 0 => {
  if (firstDate === secondDate) return 0;
  if (!firstDate) return emptyLast ? 1 : -1;
  if (!secondDate) return emptyLast ? -1 : 1;
  const firstDateObj = format === 'ISO' ? DateTime.fromISO(firstDate) : DateTime.fromFormat(firstDate, format);
  const secondDateObj = format === 'ISO' ? DateTime.fromISO(secondDate) : DateTime.fromFormat(secondDate, format);
  return firstDateObj < secondDateObj ? -1 : 1;
};

// 2020-02-11
export const compareISODates = (firstDate: string, secondDate: string) =>
  compareDatesFormat(firstDate, secondDate, 'ISO');

// 2020-02-11
export const compareChartDates = (firstDate: string, secondDate: string) =>
  compareDatesFormat(firstDate, secondDate, 'yyyy-MM-dd');

// 11. 2. 2020
export const compareCzechDates = (firstDate: string, secondDate: string) =>
  compareDatesFormat(firstDate, secondDate, 'd. M. yyyy');

// duben 2020
export const compareMonthYearFormat = (firstDate: string, secondDate: string) =>
  compareDatesFormat(firstDate, secondDate, 'LLLL yyyy');

export const searchInStrings = (search: string, strings: string[]) => {
  const normalizedSearch = search.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const regex = new RegExp([search, normalizedSearch].join('|'), 'i');

  return regex.test(strings.join(' '));
};

export const compareAccountDates = (firstDate: AccountDate, secondDate: AccountDate) =>
  compareDatesFormat(firstDate?.date, secondDate?.date, 'yyyy-MM-dd', true);

function arrayMove(arr: Array<any>, fromIndex: number, toIndex: number) {
  const element = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, element);
}

export const sortCountries = (list: { value: string; label: string }[]): { value: string; label: string }[] => {
  list.sort((a, b) => a.label.localeCompare(b.label));

  let index = -1;

  index = list.findIndex((e) => e.value === 'POL');
  index > -1 && arrayMove(list, index, 0);

  index = list.findIndex((e) => e.value === 'SVK');
  index > -1 && arrayMove(list, index, 0);

  index = list.findIndex((e) => e.value === 'CZE');
  index > -1 && arrayMove(list, index, 0);

  return list;
};

export const translatePortfolioCode = (code: PortfolioCode): string => {
  /*
  const re = new RegExp('([A-Z]{3})_([0-9]*)(_?)MP([0-9]*)');
  const parsed = re.exec(code);

  if (!parsed) return '';

  if (parsed[1] && [PortfolioType.STD, PortfolioType.ESG, PortfolioType.BAS].includes(parsed[1] as PortfolioType)) {
    // STD_MP1, ESG_MP1, BAS_MP1
    return i18next.t(code);
  } else if (parsed[2]?.length >= 2 && parsed[4]) {
    // CUS_101_MP1 => STD, inv. prov.
    return i18next.t(`STD_MP${parsed[4]}`);
  } else if (parsed.length >= 4) {
    // CUS_1_MP1, advisors in group
    return i18next.exists(code) ? i18next.t(code) : `${i18next.t('GENERIC_' + parsed[1])} ${parsed[2]} RP${parsed[4]}`;
  } else return '';
  */
  return code.title || '';
};

export const translatePortfolioType = (code: string, full?: boolean): string => {
  if (code === PortfolioType.TPL) return i18next.t(`TYPE_TPL`); // template
  if (code === PortfolioType.IND) return i18next.t(`TYPE_IND${full ? '_title' : ''}`); // IND

  const re = new RegExp('([A-Z]{3})_([0-9]*)(_?)(.*)');
  const parsed = re.exec(code);

  if (!parsed) return '';

  if (parsed[1] && [PortfolioType.STD, PortfolioType.ESG, PortfolioType.BAS].includes(parsed[1] as PortfolioType)) {
    // STD_MP1, ESG_MP1, BAS_MP1
    return i18next.t(`TYPE_${parsed[1]}${full ? '_title' : ''}`);
  } else if (parsed[2]?.length === 4) {
    // CUS_1001+ - CUSTOM/CUSTOM
    return i18next.t(`TYPE_OTHER${full ? '_title' : ''}`);
  } else if (parsed[2]?.length >= 2 && parsed[4]) {
    // CUS_101_MP1 => STD, inv. prov.
    return i18next.t(`TYPE_STD${full ? '_title' : ''}`);
  } else if (parsed.length >= 2 && full) {
    // CUS_1_MP1, advisors in group
    return i18next.t(`TYPE_CUS_title`);
  } else if (parsed.length >= 2 && !full) {
    // CUS_1_MP1, advisors in group
    return i18next.exists(`TYPE_CUS_${parsed[2]}`) ? i18next.t(`TYPE_CUS_${parsed[2]}`) : i18next.t('TYPE_CUS_GENERIC');
  } else return '';
};

export const fingerprint = (data: { assetCategories: AssetCategory[] }) => {
  let print = '';
  for (const assetCategory of data.assetCategories) {
    print += assetCategory.code;
    for (const assetClass of assetCategory.assetClasses) {
      print += assetClass.code;
      for (const asset of assetClass.assets || []) {
        print += `${asset.code}${asset.share}`;
      }
    }
  }
  return print;
};

export const compareAmountValues = (a: AmountValue, b: AmountValue, c = 1) => {
  return a.value * (a.currency === Currency.CZK ? 1 : c) - b.value * (b.currency === Currency.CZK ? 1 : c);
};

export const removeItems = (arr: string[], rem: string[]) => {
  const newArray = arr.slice();
  for (const r of rem) {
    const index = newArray.indexOf(r);
    index && newArray.splice(index, 1);
  }
  return newArray;
};

export const outOfLimits = (value: number, minMax?: { min: number; max: number }) => {
  if (!minMax) return false;
  return value / 100 < minMax.min || value / 100 > minMax.max;
};

export const outOfLimitsText = (value: number, minMax?: { min: number; max: number }) => {
  if (!outOfLimits(value, minMax)) return undefined;
  return i18next.t('general_minMaxValues',{ min: f2p(minMax!.min), max: f2p(minMax!.max) });
};
